<template>
  <div class="special-btn" :class="[type.replace(' ', '-')]">
    <q-btn :class="{ active }" unelevated v-bind="$attrs" no-caps v-on="$listeners">
      <!-- @slot Default slot for button content -->
      <slot />
    </q-btn>
  </div>
</template>

<script>
/**
 * This is a component for using custom buttons
 */
export default {
  inheritAttrs: false,
  props: {
    /**
     *  the type of the button
     */
    type: {
      type: String,
      required: true,
      validator: value =>
        ['certain', 'to renew', 'likely', 'not likely', 'for cancel', 'to cancel', 'undecided', 'unknown'].includes(
          value
        )
    },

    active: Boolean
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/fonts';
@import '~@/styles/shadows';
@import '~@/styles/general';

@mixin common-state {
  ::v-deep .q-btn {
    background: var(--r-white-2);
    color: var(--r-secondary-1);

    &:hover {
      filter: drop-shadow(0px 4px 6px rgba(71, 71, 71, 0.1));
    }

    &[disabled] {
      background: var(--r-white-2);
      opacity: 0.6;
      color: var(--r-grey-2);
      @extend .text-lowercase-btn;
    }

    @content;
  }
}

@mixin certain {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px rgba(0, 130, 142, 0.25);
      background: var(--r-graph-certain);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-certain);
      color: var(--r-white-1);
    }
  }
}

@mixin to-renew {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px rgba(0, 134, 163, 0.25);
      background: var(--r-graph-to-renew);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-to-renew);
      color: var(--r-white-1);
    }
  }
}

@mixin likely {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px #21adcb;
      background: var(--r-graph-likely);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-likely);
      color: var(--r-white-1);
    }
  }
}

@mixin not-likely {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px #1ba9c8;
      background: var(--r-graph-not-likely);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-not-likely);
      color: var(--r-white-1);
    }
  }
}

@mixin for-cancel {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px rgba(119, 122, 149, 0.5);
      background: var(--r-graph-for-cancel);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-for-cancel);
      color: var(--r-white-1);
    }
  }
}

@mixin to-cancel {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px rgba(20, 40, 80, 0.2);
      background: var(--r-graph-to-cancel);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-to-cancel);
      color: var(--r-white-1);
    }
  }
}

@mixin undecided {
  @include common-state {
    &:focus {
      box-shadow: inset 0 4px 4px rgba(0, 0, 0, 0.25);
      background: var(--r-graph-undecided);
      color: var(--r-white-1);
    }

    &:active,
    &.active {
      background: var(--r-graph-undecided);
      color: var(--r-white-1);
    }
  }
}

.special-btn {
  $height: 32px;
  --width: 66px;

  &.certain {
    @include certain;
  }

  &.to-renew {
    @include to-renew;
  }

  &.likely {
    @include likely;
  }

  &.not-likely {
    @include not-likely;
  }

  &.for-cancel {
    @include for-cancel;
  }

  &.to-cancel {
    @include to-cancel;
  }

  &.undecided,
  &.unknown {
    @include undecided;
  }

  ::v-deep .q-btn {
    @extend .border-radius-sm;
    width: var(--width);
    height: $height;

    .q-focus-helper {
      display: none;
    }

    &__wrapper {
      padding: 0;
    }

    &__content {
      height: $height;
      @extend .text-caption-2;
      transition: none;
    }

    &[disabled] {
      pointer-events: none;
      opacity: 0.6 !important;
      background: var(--r-white-2);

      .q-btn__content {
        color: var(--r-grey-2);
        @extend .text-lowercase-btn;
      }

      &.active {
        background: var(--r-white-4);

        .q-btn__content {
          @extend .text-caption-2;
        }
      }
    }
  }
}
</style>

<docs>
### Certain button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="certain">certain</SpecialBtn>
  <SpecialBtn disable type="certain">certain</SpecialBtn>
</Grid>
```

### To renew button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="to renew">to renew</SpecialBtn>
  <SpecialBtn disable type="to renew">to renew</SpecialBtn>
</Grid>
```

### Likely button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="likely">likely</SpecialBtn>
  <SpecialBtn disable type="likely">likely</SpecialBtn>
</Grid>
```

### Not likely button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="not likely">not likely</SpecialBtn>
  <SpecialBtn disable type="not likely">not likely</SpecialBtn>
</Grid>
```

### For cancel button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="for cancel">for cancel</SpecialBtn>
  <SpecialBtn disable type="for cancel">for cancel</SpecialBtn>
</Grid>
```

### To cancel button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="to cancel">to cancel</SpecialBtn>
  <SpecialBtn disable type="to cancel">to cancel</SpecialBtn>
</Grid>
```

### Undecided button

```jsx
<Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
  <SpecialBtn type="undecided">undecided</SpecialBtn>
  <SpecialBtn disable type="undecided">undecided</SpecialBtn>
</Grid>
```
</docs>
